<!-- 
화면명 : LateFeeList.vue
작성일 : 2025-08-05
설명   : 연체료 현황 화면
-->
<template>
  <div class="relative bg-blueGray-100">
    <div class="flex justify-between mt-2">
      <div class="flex text-black font-bold text-lg items-center justify-center">
        <img src="@/assets/img/breadcrumbs.svg" class="w-4 h-4 mx-2" />
        연체료 현황
      </div>
      <div class="flex mx-2">
        <UCButton label="조회" width="80px" icon="search" @click="btnSearchClick" />
        <UCButton label="엑셀" width="80px" icon="excel" @click="btnExcelClick" />
        <UCButton label="출력" width="80px" icon="printer"  @click="btnReportClick" />
      </div>
    </div>

    <div class="bg-white p-1 rounded shadow mt-2 top-wrapper">
      <div
        class="w-full overflow-x-visible inline-flex flex-wrap items-center gap-6 whitespace-nowrap px-4 py-2 border rounded bg-white shadow-sm"
      >
        <div class="flex items-center space-x-2">
          <UCVendor
            title="거래선"
            :VendCd="userStore.userVendCd"
            :VendName="userStore.userName"
          />
        </div>

        <UCRadio
          label="구분"
          v-model="rdoGubun.cd"
          :arrData="rdoGubun.arr"
          code="value"
          name="label"
        />

        <div class="flex space-x-2">
          <UCDate
            label="대상월"
            v-model="dtStart"
            format="yyyy-MM"
            month-picker
          />
        </div>

        <UCRadio
          label="금액구분"
          v-model="rdoAmtGubun.cd"
          :arrData="rdoAmtGubun.arr"
          code="value"
          name="label"
        />
      </div>
    </div>

    <!-- 소계 구분 -->
    <div class="mt-2 flex justify-between items-center">
      <label class="flex text-blueGray-600 text-base font-semibold whitespace-nowrap items-center justify-center" >
        <img src="@/assets/img/icon-list.svg" class="flex w-5 h-5 mr-2" />
        연체료현황리스트
      </label>

      <UCRadio
        label="소계구분"
        v-model="rdoSubTotalGubun.cd"
        :arrData="rdoSubTotalGubun.arr"
        code="value"
        name="label"
      />
    </div>

    <!-- 그리드 -->
    <div class="mt-2 mb-2 flex-1 overflow-y-auto h-full">
      <UCGrid
        :data="arrSearchData"
        :isRowNumber="true"
        :columns="columns"
        :selectable="true"
        :pagination="true"
        :paginationSize="50"
        autoHeight
        :excludeSelectors="['.top-wrapper']"
        :heightOffset="60"
        @row-selected="handleSelection"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { format as formatDate } from "date-fns";
import { notify } from "@kyvg/vue3-notification";

import { useUserStore } from "@/store/userStore";
import { saveAs } from "file-saver";
import ExcelJS from "exceljs";

import UCVendor from "@/components/UserControl/UCVendor.vue";
import UCGrid from "@/components/UserControl/UCGrid.vue";
import UCText from "@/components/UserControl/UCText.vue";
import UCCombo from "@/components/UserControl/UCCombo.vue";
import UCDate from "@/components/UserControl/UCDate.vue";
import UCRadio from "@/components/UserControl/UCRadio.vue";
import UCButton from "@/components/UserControl/UCButton.vue";

import { apiExecuteDataSet, apiExecuteReport } from "@/api/SCM/common";

const userStore = useUserStore();

// 구분
const rdoGubun = ref({
  cd: "",
  arr: [
    { label: "전체", value: "" },
    { label: "자재", value: "M" },
    { label: "외주", value: "P" },
  ],
});

const dtStart = ref("");

// 금액 구분
const rdoAmtGubun = ref({
  cd: "",
  arr: [
    { label: "전체", value: "" },
    { label: "1,000원 이상", value: "Y" },
  ],
});

// 소계 구분
const rdoSubTotalGubun = ref({
  cd: "TOTAL",
  arr: [
    { label: "전체", value: "TOTAL" },
    { label: "거래선별", value: "CUSNO" },
    { label: "입고일자별", value: "IPDATE" },
    { label: "호선별", value: "WORKNO" },
  ],
});

onMounted(() => {
  // console.log("mounted");

  bindCombo();

  btnSearchClick();
});

async function bindCombo() {
  // console.log("bindCombo");

  const today = new Date();
  dtStart.value = formatDate(today, "yyyy-MM");
}

async function btnSearchClick() {
  // console.log("btnSearchClick");

  const searchParams = {
    
    ID: "WERP\\T_MT06IPGR\\R106",
    Parameter: {
      "@DELAYDATAE": dtStart.value.replaceAll("-", ""),
      "@SGBN": rdoGubun.value.cd,
      "@PROGBN": rdoAmtGubun.value.cd,
      "@CUSNO": userStore.userVendCd,
      "@SUBTOTALGBN": rdoSubTotalGubun.value.cd,

      "@PAGENO": 1,
      "@PAGEINROW": 10000,
      "@SIDX": null,
      "@SORD": null,
      "@CURPAGE": null,
      "@MAXPAGE": null,
      "@TOTALCOUNT": null,
    },
  };

  const res = await apiExecuteDataSet(searchParams);
  // console.log("res", res._Result);

  arrSearchData.value = res._Result[0];
}

const arrSearchData = ref([]);
const selectedRows = ref([]);
function handleSelection(data) {
  selectedRows.value = data;
  console.log("선택된 행:", data);
}

const columns = [
  { title: "입고번호", field: "IPGKEY", width: 120, hozAlign: "center" },
  { title: "구분", field: "GBN", width: 80, hozAlign: "center" },
  { title: "자재명", field: "M06MATNA", width: 190, hozAlign: "left" },
  { title: "규격", field: "M06SIZE", width: 110, hozAlign: "left" },
  { title: "재질", field: "M06MATL", width: 110, hozAlign: "left" },
  { title: "도면번호", field: "M06DWGNO", width: 100, hozAlign: "left" },
  { title: "공사번호", field: "WORKNO", width: 120, hozAlign: "left" },
  { title: "발주번호", field: "M06ORDKEY", width: 120, hozAlign: "center" },
  { title: "납품장소", field: "DEVNAME", width: 120, hozAlign: "left" },
  { title: "거래선", field: "M06CUSNO", width: 100, hozAlign: "center" },
  { title: "거래선명", field: "M01CUSNAME", width: 120, hozAlign: "left" },

  { title: "단위", field: "M06UNIT", width: 80, hozAlign: "center" },
  { title: "입고량", field: "M06IPGQTY", width: 100, hozAlign: "right" ,
        formatter: "money",
        formatterParams: {
          thousand: ",",
          precision: 0,
        }, },
  { title: "단가", field: "M06DANGA", width: 90, hozAlign: "right" ,
        formatter: "money",
        formatterParams: {
          thousand: ",",
          precision: 0,
        }, },
  { title: "금액", field: "M06AMOUNT", width: 120, hozAlign: "right"  ,
        formatter: "money",
        formatterParams: {
          thousand: ",",
          precision: 0,
        },},
  { title: "납기일자", field: "M05DEVDATE", width: 120, hozAlign: "center" },
  { title: "입고일자", field: "M06INDATE", width: 120, hozAlign: "center" },
  { title: "입고납기", field: "M06DEVDATE", width: 120, hozAlign: "center" },
  { title: "일수", field: "DEVDAY", width: 70, hozAlign: "center" },
  { title: "연체금액", field: "DLYAMT", width: 120, hozAlign: "right"  ,
        formatter: "money",
        formatterParams: {
          thousand: ",",
          precision: 0,
        },},
];

async function btnExcelClick() {
  // console.log("btnExcelClick");

  // 다시 조회를 해서 가져오는게 나을지 확인 -> 조회랑 데이터가 달라서 다시 조회
  const searchParams = {
    
    ID: "WERP\\T_MT06IPGR\\R106",
    Parameter: {
      "@DELAYDATAE": dtStart.value.replaceAll("-", ""),
      "@SGBN": rdoGubun.value.cd,
      "@PROGBN": rdoAmtGubun.value.cd,
      "@CUSNO": userStore.userVendCd,
      "@SUBTOTALGBN": rdoSubTotalGubun.value.cd,

      "@PAGENO": 1,
      "@PAGEINROW": 10000,
      "@SIDX": null,
      "@SORD": null,
      "@CURPAGE": null,
      "@MAXPAGE": null,
      "@TOTALCOUNT": null,
    },
  };

  const res = await apiExecuteDataSet(searchParams);
  // console.log("res", res._Result);

  if (res._Result[0].length === 0) {
    notify({
      title: "실패",
      text: "조회된 데이터 건수가 없습니다.",
      type: "error",
    });
    return;
  }

  const templatePath = "/excel/연체료현황.xlsx";
  const today = new Date();

  try {
    // 템플릿 파일 가져오기
    const response = await fetch(templatePath);
    const buffer = await response.arrayBuffer();

    // 워크북 로드
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(buffer);

    // 첫 번째 워크시트 선택
    const worksheet = workbook.getWorksheet(1);

    // 데이터 바인딩
    worksheet.getCell("A2").value = `납기일자 : ${
      dtStart.value.replaceAll("-", "").slice(0, 4) +
      "년 " +
      dtStart.value.replaceAll("-", "").slice(4, 6) +
      "월"
    }`;
    worksheet.getCell("A3").value = `거래처 : ${
      userStore.userVendCd.substring(0, 3) +
      "-" +
      userStore.userVendCd.substring(4, 2) +
      "-" +
      userStore.userVendCd.substring(6)
    }`;
    worksheet.getCell("A4").value = `구  분 : ${
      rdoAmtGubun.value.cd === "Y" ? "1,000원이상" : "전체"
    }`;

    worksheet.getCell("T2").value = `작성자 : ${userStore.userName}`;
    worksheet.getCell("T3").value = `출력일자 :  ${formatDate(
      today,
      "yyyy-MM-dd"
    )}`;

    // 테이블 데이터 바인딩
    const startRow = 7; // 데이터가 시작될 행 번호 (예: 6번째 행부터 시작)
    res._Result[0].forEach((item, index) => {
      // console.log("forEach", index, item);

      const row = worksheet.getRow(startRow + index);
      row.getCell(1).value = item.IPGKEY;
      row.getCell(2).value = item.GBN;
      row.getCell(3).value = item.M06MATNA;
      row.getCell(4).value = item.M06SIZE;
      row.getCell(5).value = item.M06MATL;
      row.getCell(6).value = item.M06DWGNO;
      row.getCell(7).value = item.WORKNO;
      row.getCell(8).value = item.M06ORDKEY;
      row.getCell(9).value = item.DEVNAME;

      row.getCell(10).value = item.M06CUSNO;
      row.getCell(11).value = item.M01CUSNAME;
      row.getCell(12).value = item.M06UNIT;
      row.getCell(13).value = item.M06IPGQTY;
      row.getCell(14).value = item.M06DANGA;
      row.getCell(15).value = item.M06AMOUNT;
      row.getCell(16).value = item.M05DEVDATE;
      row.getCell(17).value = item.M06INDATE;
      row.getCell(18).value = item.M06DEVDATE;
      row.getCell(19).value = item.DEVDAY;

      row.getCell(20).value = item.DLYAMT;
    });

    // 합계 데이터 바인딩
    // worksheet.getCell('D' + (startRow + dataToBind.salesData.length)).value = dataToBind.totalSales;

    // 변경된 워크북을 버퍼로 변환
    const excelBuffer = await workbook.xlsx.writeBuffer();

    // 파일을 Blob으로 만들고 다운로드
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, `연체료현황_${formatDate(today, "yyyyMMdd")}.xlsx`);

    notify({
      title: "성공",
      text: "엑셀 다운로드가 완료되었습니다.",
      type: "success",
    });
  } catch (error) {
    // console.error("엑셀 다운로드 중 오류 발생:", error);

    notify({
      title: "실패",
      text: "엑셀 다운로드에 실패했습니다." + error,
      type: "error",
    });
  }
}


//연체료현황
async function btnReportClick() {
  const searchParams = {
    
    ID: "W000\\W000\\R-OCM\\MT33PSR",
    Parameter: {
      "@sUSRID": userStore.userVendCd,
      "@sDT1": dtStart.value.replaceAll("-", "")+'01',
      "@sDT2": dtStart.value.replaceAll("-", "")+'31',
      "@sGBN": rdoAmtGubun.value.cd,
    },
  };

  const exportParams = {
    title: "연체료현황",
    //width: 1100,
    //height: 900,
  };

  await apiExecuteReport(searchParams, exportParams);
}
</script>
